/**
 * Sprint 41 §14.S10-Q1 — shared type contracts for the
 * `<ProjectLayoutSections>` umbrella component.
 *
 * The umbrella renders Sections II–VI of the Phase 0 §11 canonical
 * 7-section layout. Section I (header) + Section VII (signatures)
 * stay page-specific per S10-Q1 lock. The umbrella is consumed by
 * four pages, each with `mode` toggling edit affordances + section
 * visibility per §14.S12-Q3 lock:
 *
 *   estimate:  estimator-facing editable view (Estimate page)
 *   review:    estimator-facing approval view (Review Screen)
 *   proposal:  customer-facing discussion artifact (no CTAs, no IV/V/VII)
 *   contract:  customer-facing binding artifact (signature + variation
 *              radio + decline button)
 *
 * Section visibility matrix (per §14.S12-Q3 + §14.S10 retroactive lock):
 *
 *   Section            | estimate | review | proposal | contract
 *   I.   Overview      |    ✓     |   ✓    |    ✓     |    ✓     (page-specific)
 *   II.  ProjectSummary|    ✓     |   ✓    |    ✓     |    ✓
 *   III. ProjectScope  |    ✓     |   ✓    |    ✓     |    ✓
 *   IV.  ContractTerms |    ✓     |   ✓    |    —     |    ✓
 *   V.   Warranty      |    ✓     |   ✓    |    —     |    ✓
 *   VI.  Investment    |    ✓     |   ✓    |    ✓     |    ✓
 *   VII. Signature     |    ✓     |   —    |    —     |    ✓     (page-specific)
 */

export type ProjectLayoutMode = 'estimate' | 'review' | 'proposal' | 'contract';

/**
 * Per-mode capability flags. Read by sub-components to toggle edit
 * affordances + visibility. Centralized here so adding a new mode is
 * a single-table change.
 */
export interface ProjectLayoutCapabilities {
  /** true when sub-components should render edit affordances */
  isEditable: boolean;
  /** true when Section IV (Contract Terms) should render */
  showContractTerms: boolean;
  /** true when Section V (Warranty) should render */
  showWarranty: boolean;
  /** true when variation blocks should show interactive selector (vs static display) */
  variationSelectionInteractive: boolean;
  /** true when cost-history side panel link should appear */
  showCostHistoryLink: boolean;
  /**
   * Founder mockup 2026-07-16 — true when Section III may render cost
   * figures (per-line-item cost rows, unit costs, "+ Add cost",
   * overhead-allocation detail). Estimator-facing modes only; the
   * customer-facing proposal/contract surfaces keep the server-side
   * redaction (unit costs / cost sources / margins never render).
   */
  showCostBreakdown: boolean;
}

export function capabilitiesForMode(
  mode: ProjectLayoutMode,
): ProjectLayoutCapabilities {
  switch (mode) {
    case 'estimate':
      return {
        isEditable: true,
        showContractTerms: true,
        showWarranty: true,
        variationSelectionInteractive: false, // estimator views; doesn't pick
        showCostHistoryLink: true,
        showCostBreakdown: true, // estimator assigns costs per line item
      };
    case 'review':
      return {
        isEditable: false,
        showContractTerms: true,
        showWarranty: true,
        variationSelectionInteractive: false,
        showCostHistoryLink: true,
        showCostBreakdown: true, // estimator-facing, read-only
      };
    case 'proposal':
      return {
        isEditable: false,
        showContractTerms: false, // §14.S12-Q3 — proposal omits IV
        showWarranty: false, // §14.S12-Q3 — proposal omits V
        variationSelectionInteractive: false, // §14.S11-Q12 revised — proposal shows priced blocks; no selector
        showCostHistoryLink: false,
        showCostBreakdown: false, // customer-facing — cost internals stay redacted
      };
    case 'contract':
      return {
        isEditable: false,
        showContractTerms: true,
        showWarranty: true,
        variationSelectionInteractive: true, // §14.S12-Q9 — first signer picks
        showCostHistoryLink: false,
        showCostBreakdown: false, // customer-facing — cost internals stay redacted
      };
    default: {
      const _exhaustive: never = mode;
      throw new Error(`Unknown ProjectLayoutMode: ${_exhaustive as string}`);
    }
  }
}

/**
 * Loose data prop for the umbrella + sub-components. The full
 * ResolvedEstimateDto type lives in `services/estimate.ts`; we accept
 * `unknown`-shaped data here so the layout module isn't tightly
 * coupled to that DTO during incremental rollout. Subsequent waves
 * tighten the type once the resolver shape is stable across all four
 * pages' consumers.
 */
export interface ProjectLayoutData {
  project: {
    id: string;
    projectName?: string | null;
    projectNumber?: string | null;
    fieldCode?: string | null;
    projectLabel?: string | null;
    summary?: string | null;
    isFinalized?: boolean;
    [k: string]: unknown;
  };
  client?: {
    id: string;
    fullName?: string | null;
    [k: string]: unknown;
  } | null;
  scopeElements?: Array<unknown>;
  projectDetails?: Array<unknown>;
  projectDetailGroups?: Array<unknown>;
  sectionTemplates?: Array<unknown>;
  investmentSummary?: unknown;
  // §14.S10-Q2b — discount lines on Investment Summary
  discountLines?: Array<unknown>;
  [k: string]: unknown;
}

/**
 * Sprint 41 W24 — line-item patch payload sent by Section III inline
 * edit. Mirrors `UpdateLineItemPayload` in `services/estimate.ts`
 * (description / quantity / unit / customUnit / purpose / isAllowance /
 * position) but kept structurally narrow here so the layout module
 * doesn't import from services. The estimate-mode page (EstimateViewV2)
 * supplies the callback; review/proposal/contract modes pass undefined.
 */
export interface LineItemPatchInput {
  description?: string;
  quantity?: string;
  unit?: string;
  customUnit?: string | null;
  purpose?: string | null;
  isAllowance?: boolean;
  position?: number;
}

export type LineItemPatchHandler = (
  lineItemId: string,
  patch: LineItemPatchInput,
) => Promise<void> | void;

/**
 * Section III line-item delete. Hard-deletes the ProjectLineItem (and
 * cascaded ProjectCost rows). Estimate mode supplies the callback;
 * other modes leave it undefined (no delete affordance). Mirrors
 * DELETE /estimate/line-items/:id.
 */
export type LineItemDeleteHandler = (
  lineItemId: string,
) => Promise<void> | void;

/**
 * Estimate drawer (founder 2026-07-30, Option 4c) — opens the
 * page-level line-item drawer for a Section III row. When supplied,
 * line-item rows render as read-only purpose-first rows [type chip |
 * Purpose | qty | unit | total] that open the drawer on click; the
 * inline cost breakdown and per-row editing move into the drawer.
 * When absent (review mode), the legacy inline rendering is kept.
 */
export type LineItemOpenHandler = (lineItemId: string) => void;

/**
 * Estimate drawer — routes "+ Add material / + Add service" into the
 * drawer's MPT search state instead of the inline add row. When
 * absent, the legacy AddSkuInline row renders.
 */
export type LineItemAddRequestHandler = (ctx: {
  elementId: string;
  fieldId: string | null;
  type: 'MATERIAL' | 'SERVICE';
}) => void;

/**
 * Founder mockup 2026-07-16 — Section III "+ Add" line item. Creates a
 * new ProjectLineItem on the element, either attached to a CSF row
 * (`projectScopeElementFieldId` set) or unattached (Administration
 * bucket, `projectScopeElementFieldId` null). Mirrors
 * `CreateLineItemPayload` in `services/estimate.ts` narrowly so the
 * layout module doesn't import from services. Estimate mode supplies
 * the callback; other modes leave it undefined (no add affordance).
 */
export interface LineItemCreateInput {
  type: 'MATERIAL' | 'SERVICE' | 'OTHER';
  projectScopeElementFieldId?: string | null;
  description: string;
  quantity: string;
  unit: string;
  /**
   * EST-L8 (2026-07-22) — the add-SKU row is critical path: Purpose is
   * one of the five required inputs, and the SKU + Unit Cost land as
   * the line item's initial ProjectCost row (productMsku for
   * materials, serviceId for services; free-entered SKUs allowed).
   * Mirrors `CreateLineItemPayload.initialCost` in services/estimate.
   */
  purpose?: string | null;
  initialCost?: {
    productMsku?: string | null;
    serviceId?: string | null;
    description: string;
    unitCostSnapshot: string;
  };
}

export type LineItemCreateHandler = (
  elementId: string,
  payload: LineItemCreateInput,
) => Promise<void> | void;

/**
 * Sprint 41 W25 — cost-row mutation handlers, bundled into one prop so
 * the umbrella doesn't sprout three independent callbacks. EstimateViewV2
 * supplies the bundle; other modes pass undefined and cost rows render
 * read-only.
 *
 * Semantics:
 *   - onUnitCostOverride(costId, value) — passes value through to the
 *     backend's `unitCostOverride` field. Pass null to clear the override
 *     and revert to the resolver snapshot (costSource flips back from
 *     OVERRIDE to whatever the cascade said).
 *   - onAdd(lineItemId, payload) — appends a new ProjectCost row at the
 *     bottom of the line item's costs. Description + unitCostSnapshot
 *     required; productMsku / serviceId optional.
 *   - onDelete(costId) — hard deletes the row.
 */
export interface CostRowHandlers {
  onUnitCostOverride: (
    costId: string,
    value: string | null,
  ) => Promise<void> | void;
  onAdd: (
    lineItemId: string,
    payload: {
      description: string;
      unitCostSnapshot: string;
      productMsku?: string | null;
      serviceId?: string | null;
    },
  ) => Promise<void> | void;
  onDelete: (costId: string) => Promise<void> | void;
}

/**
 * Sprint 41 W27 — invoked when the user clicks the history icon on a
 * cost row. The page owns the side-panel state; the umbrella just
 * forwards the request. `lineItemId` is what the W21 endpoint takes;
 * `costId` lets the page scroll to / highlight the relevant group on
 * open.
 */
export type ShowCostHistoryHandler = (
  lineItemId: string,
  costId: string,
) => void;

/**
 * Sprint 41 W28 — invoked when the user saves or resets the Section IV
 * Contract Terms per-project override. Pass null to clear the override
 * and revert to the admin-layer CONTRACT_TERMS SectionTemplate. The
 * page wires this to the existing PATCH /estimate/:projectId/contract
 * endpoint (which already accepts contractTermsOverride per W23).
 */
export type ContractTermsOverrideHandler = (
  override: string | null,
) => Promise<void> | void;

/**
 * Sprint 41 W32 enrichment — Section II Assumptions editor.
 * Restores the `AssumptionsField` rich-text editor + reset that lived
 * on the legacy /estimate page before the W32 umbrella cutover.
 * Writes to `Project.assumptionsOverride` via the existing
 * PATCH /estimate/:projectId/contract endpoint
 * (`estimateApi.updateContract({ assumptionsOverride })`). Pass null
 * to clear the override (project falls back to admin-layer default).
 *
 * Same shape as ContractTermsOverrideHandler — separate type so the
 * two are independently swappable and audit-trail-distinguishable.
 */
export type AssumptionsOverrideHandler = (
  override: string | null,
) => Promise<void> | void;

/**
 * Sprint 41 W29 — Section VI discount-line CRUD handlers. Same bundle
 * pattern as CostRowHandlers. EstimateViewV2 supplies the bundle;
 * other modes pass undefined and the discount-line UI stays read-only.
 *
 * Semantics map 1:1 onto the W22 backend endpoints:
 *   - onAdd → POST /estimate/:projectId/discounts
 *   - onUpdate → PATCH /estimate/discounts/:id
 *   - onDelete → DELETE /estimate/discounts/:id
 */
/**
 * Task 18 Option B — include / phase an element. Project Details is
 * locked on; this only runs for real scope elements.
 */
export type ElementInclusionHandler = (
  elementId: string,
  clientIncluded: boolean,
) => Promise<void> | void;

/** Task 18 — persist a row margin % (element or Project Details). */
export type ElementMarginHandler = (
  elementId: string,
  marginPercentage: string,
) => Promise<void> | void;

export type ProjectDetailsMarginHandler = (
  marginPercentage: string,
) => Promise<void> | void;

export interface DiscountHandlers {
  onAdd: (payload: {
    appliesTo: 'PROJECT' | 'ELEMENT';
    description: string;
    amount: string;
    projectScopeElementId?: string | null;
  }) => Promise<void> | void;
  onUpdate: (
    discountId: string,
    patch: {
      appliesTo?: 'PROJECT' | 'ELEMENT';
      description?: string;
      amount?: string;
      projectScopeElementId?: string | null;
    },
  ) => Promise<void> | void;
  onDelete: (discountId: string) => Promise<void> | void;
}

/**
 * Sprint 41 W30 — Section VI payment-milestones save handler.
 * Persists an ordered list { ref, payment, milestone, pct } on
 * Project.paymentMilestones via the existing
 * PATCH /estimate/:projectId/contract endpoint (which has accepted
 * paymentMilestones since Sprint 36). Pass null to revert to the
 * Settings-level default template.
 *
 * pct values are fractions (0.20 == 20%) and should sum to 1. The
 * editor validates this client-side before calling the handler.
 */
export interface PaymentMilestone {
  ref: number;
  payment: string;
  milestone: string;
  pct: number;
}

export type PaymentMilestonesHandler = (
  milestones: PaymentMilestone[] | null,
) => Promise<void> | void;

/**
 * Sprint 41 W32 enrichment Slice 5 — Section VI manual allowances.
 * Restores the AllowancesSummary card that lived on the pre-W32
 * /estimate page. Writes to `Project.allowances`
 * (hoisted in W35-cutover Slice 1) via the existing
 * PATCH /estimate/:projectId/contract endpoint.
 *
 * Pass `null` to clear the list; pass an empty array to keep it
 * present-but-empty (the FE treats both as "no allowances" but
 * the distinction lets backend audits tell apart "unset" vs
 * "explicitly cleared").
 */
export interface AllowanceEntry {
  type: string;
  description: string;
  amount: string;
}

export type AllowancesHandler = (
  allowances: AllowanceEntry[] | null,
) => Promise<void> | void;

/**
 * Sprint 41 W32 enrichment — Section II Timeline editor.
 * Restores the `TimelineField` editor that lived on the legacy
 * `/estimate` page before the W32 umbrella cutover. Writes to
 * `Project.weeksUntilStart` + `weeksToComplete` + `tentativeStartDate`
 * via the existing `PATCH /estimate/:projectId/contract` endpoint
 * (`estimateApi.updateContract` on the FE). Pass any field as `null`
 * to clear it.
 *
 * Only rendered when `caps.isEditable` is true AND the handler is
 * supplied. Read-only modes (review / proposal / contract) show the
 * resolved values inline.
 */
export interface TimelineInput {
  weeksUntilStart?: number | null;
  weeksToComplete?: number | null;
  tentativeStartDate?: string | null;
}

export type TimelineHandler = (
  patch: TimelineInput,
) => Promise<void> | void;

/**
 * Sprint 41 W32 enrichment Slice 3 — Section II AI summary generate.
 * Restores the SummaryField "Generate" button that lived on the
 * pre-W32 /estimate page. The handler calls
 * POST /estimate/:projectId/generate-summary (already exposed via
 * `estimateApi.generateSummary`) and is expected to refresh the
 * page's layout data so the new narrative lands in the same editor.
 *
 * Must be invoked only on an explicit click — never on mount.
 * Returns the freshly generated summary text so the section can put
 * it in the rich-text box before the reload completes.
 */
export type GenerateSummaryHandler = () => Promise<{ summary: string }>;

/**
 * Founder 2026-09-01 — Section II Narrative save. Writes the estimator's
 * (or AI-then-edited) HTML to `Project.summary` via
 * PATCH /estimate/:projectId/contract (`estimateApi.updateContract`).
 * Pass null to clear.
 */
export type SummaryOverrideHandler = (
  summary: string | null,
) => Promise<void> | void;

/** Section II Project Name inline edit. */
export type ProjectNameHandler = (projectName: string) => Promise<void> | void;

/**
 * Sprint 41 W32 enrichment Slice 4 (Wave 4e) — DetailsTable inline edit.
 *
 * Called when an estimator commits an inline edit on a Section II
 * Project Detail row. `value` is `unknown` per the locked design (Q1 A
 * — value-only edit; label and key are immutable from this surface).
 * Backend persists as Prisma JSON + emits a per-cell AuditEvent.
 *
 * Implementations wire to `PATCH /estimate/details/:detailId/value`.
 */
export type DetailsTableHandler = (input: {
  detailId: string;
  value: unknown;
}) => Promise<void>;

/**
 * After a Project Detail line-item / cost mutation (Section II Site
 * Conditions / Special Requirements / …). The estimate page reloads
 * the resolve payload so `lineItems` + `rowTotal` re-render.
 */
export type DetailsMutatedHandler = () => void | Promise<void>;

/**
 * Founder requirement 2026-07 — Section III CSF selection inline edit.
 *
 * "The CSFs populate on the estimate. The estimator shall be able to
 * change the selections." Called when an estimator commits an inline
 * edit on a scope-element CSF row's value. Value-only per the same
 * design lock-in as DetailsTableHandler — label and scfCode are
 * immutable from this surface. The backend validates the value against
 * the linked intake question's ACTIVE version (422 on an invalid
 * option code), emits an ESTIMATE_FIELD_VALUE_EDITED AuditEvent, and
 * rejects once the estimate is finalized.
 *
 * Implementations wire to `PATCH /estimate/fields/:fieldId/value` and
 * are expected to refresh the resolve payload so the re-rendered
 * scope statements pick up the new selection.
 */
export type ScopeFieldValueHandler = (input: {
  fieldId: string;
  value: unknown;
}) => Promise<void>;
